import { useEffect, useState } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';
import { useTranslation } from '../../../core/hooks/useTranslation';
import { SearchFilters } from '../hooks/useSearch';
import { getSearchMeta } from '../../../core/api/client';
import { usePublicCategories } from '../../../hooks/useCategories';

interface FilterSidebarProps {
    filters: SearchFilters;
    onUpdate: (key: keyof SearchFilters, value: any) => void;
    className?: string;
    isOpen: boolean;
    onClose: () => void;
}

interface PropertyType {
    slug: string;
    name: string;
    icon?: string;
}

interface SearchMeta {
    minArea: number;
    maxArea: number;
    minPrice: number;
    maxPrice: number;
    types: PropertyType[];
    locations: string[];
    constructionStatuses?: { id: string, labelKey: string }[];
    finishTypes?: { id: string, labelKey: string }[];
}

export const FilterSidebar = ({ filters, onUpdate, className = '', isOpen, onClose }: FilterSidebarProps) => {
    const { t, i18n } = useTranslation();
    const { data: categories } = usePublicCategories();
    const lang = i18n.language.startsWith('ar') ? 'ar' : 'en';

    const [meta, setMeta] = useState<SearchMeta>({
        minArea: 0,
        maxArea: 1000,
        minPrice: 0,
        maxPrice: 100000000,
        types: [],
        locations: [],
        constructionStatuses: [],
        finishTypes: []
    });

    useEffect(() => {
        getSearchMeta().then(res => {
            if (res.success) {
                setMeta(res.data);
            }
        }).catch(err => console.error(err));
    }, [i18n.language]);

    const toggleFilter = (key: 'location' | 'category' | 'beds' | 'baths' | 'constructionStatus' | 'finishType', value: string) => {
        const current = filters[key] || [];
        const next = current.includes(value)
            ? current.filter(item => item !== value)
            : [...current, value];
        onUpdate(key, next);
    };

    const isSelected = (key: 'location' | 'category' | 'beds' | 'baths' | 'constructionStatus' | 'finishType', value: string) =>
        filters[key]?.includes(value);

    // Fallback types if API fails or returns empty
    // const fallbackTypes: PropertyType[] = ['apartment', 'villa', 'duplex', 'twinhouse', 'townhouse'].map(slug => ({
    //     slug,
    //     name: t(`propertyConfig.types.${slug}`, slug)
    // }));

    // const displayTypes = meta.types.length > 0 ? meta.types : fallbackTypes;

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={onClose}
                />
            )}

            <aside className={`
                fixed inset-y-0 left-0 z-50 w-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:w-64 md:shadow-none md:bg-transparent md:block
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                ${className}
            `}>
                <div className="h-full overflow-y-auto scrollbar-hide pb-20 md:pb-0 px-6 md:px-0 bg-white md:bg-transparent">
                    {/* Header */}
                    <div className="flex items-center justify-between py-6 md:hidden">
                        <h2 className="text-xl font-bold text-secondary">{t('search.filters.title', 'Filters')}</h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* Locations */}
                        <FilterSection
                            title={t('search.filters.locations', 'Locations')}
                            onReset={() => onUpdate('location', [])}
                            hasSelection={filters.location.length > 0}
                            t={t}
                        >
                            <div className="space-y-2">
                                {meta.locations.map(loc => (
                                    <label key={loc} className="flex items-center gap-3 cursor-pointer group">
                                        <div className={`
                                            w-5 h-5 rounded border flex items-center justify-center transition-colors
                                            ${isSelected('location', loc)
                                                ? 'bg-secondary border-secondary text-white'
                                                : 'border-gray-300 group-hover:border-secondary'}
                                        `}>
                                            {isSelected('location', loc) && <Check className="w-3.5 h-3.5" />}
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={isSelected('location', loc)}
                                            onChange={() => toggleFilter('location', loc)}
                                        />
                                        <span className={`text-sm ${isSelected('location', loc) ? 'font-medium text-secondary' : 'text-gray-600'}`}>
                                            {loc}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </FilterSection>

                        {/* Category Filter (Hierarchical) */}
                        <FilterSection
                            title={t('search.filters.category', 'Category')}
                            onReset={() => onUpdate('category', [])}
                            hasSelection={filters.category?.length > 0}
                            t={t}
                        >
                            <div className="space-y-2">
                                {categories?.map((cat: any) => {
                                    const getAllIds = (category: any): string[] => {
                                        let ids = [category.id];
                                        if (category.children) {
                                            category.children.forEach((c: any) => ids.push(...getAllIds(c)));
                                        }
                                        return ids;
                                    };

                                    const isCatSelected = isSelected('category', cat.id);

                                    const toggleCategory = (category: any) => {
                                        const idsToToggle = getAllIds(category);
                                        const currentlySelected = filters.category || [];
                                        const isSelected = currentlySelected.includes(category.id);

                                        let next = [...currentlySelected];
                                        if (isSelected) {
                                            next = next.filter(id => !idsToToggle.includes(id));
                                        } else {
                                            next = Array.from(new Set([...next, ...idsToToggle]));
                                        }
                                        onUpdate('category', next);
                                    };

                                    const getName = (c: any) => c.translations?.find((t: any) => t.locale === lang)?.name || c.translations?.find((t: any) => t.locale === 'en')?.name || c.slug;

                                    return (
                                        <div key={cat.id} className="space-y-2">
                                            {/* Parent */}
                                            <label className="flex items-center gap-3 cursor-pointer group">
                                                <div className={`
                                                    w-5 h-5 rounded border flex items-center justify-center transition-colors
                                                    ${isCatSelected
                                                        ? 'bg-secondary border-secondary text-white'
                                                        : 'border-gray-300 group-hover:border-secondary'}
                                                `}>
                                                    {isCatSelected && <Check className="w-3.5 h-3.5" />}
                                                </div>
                                                <input
                                                    type="checkbox"
                                                    className="hidden"
                                                    checked={isCatSelected}
                                                    onChange={() => toggleCategory(cat)}
                                                />
                                                <span className={`text-sm ${isCatSelected ? 'font-medium text-secondary' : 'text-gray-600'}`}>
                                                    {getName(cat)}
                                                </span>
                                            </label>

                                            {/* Children */}
                                            {cat.children && cat.children.length > 0 && (
                                                <div className={`
                                                    space-y-2 mt-2
                                                    ${lang === 'ar' ? 'mr-[0.6rem] border-r-2 pr-4' : 'ml-[0.6rem] border-l-2 pl-4'}
                                                    border-gray-300
                                                `}>
                                                    {cat.children.map((child: any) => {
                                                        const isChildSelected = isSelected('category', child.id);
                                                        return (
                                                            <div key={child.id} className="relative flex items-center">
                                                                {/* Connector Line */}
                                                                <div className={`
                                                                    absolute top-1/2 -translate-y-1/2
                                                                    h-[2px] bg-gray-300 w-4
                                                                    ${lang === 'ar' ? '-right-[1.1rem]' : '-left-[1.1rem]'}
                                                                `}></div>

                                                                <label className="flex items-center gap-3 cursor-pointer group flex-1">
                                                                    <div className={`
                                                                        w-4 h-4 rounded border flex items-center justify-center transition-colors
                                                                        z-10
                                                                        ${isChildSelected
                                                                            ? 'bg-secondary border-secondary text-white'
                                                                            : 'bg-white border-gray-300 group-hover:border-secondary'}
                                                                    `}>
                                                                        {isChildSelected && <Check className="w-3 h-3" />}
                                                                    </div>
                                                                    <input
                                                                        type="checkbox"
                                                                        className="hidden"
                                                                        checked={isChildSelected}
                                                                        onChange={() => toggleCategory(child)}
                                                                    />
                                                                    <span className={`text-sm ${isChildSelected ? 'font-medium text-secondary' : 'text-gray-600'}`}>
                                                                        {getName(child)}
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </FilterSection>

                        {/* Bedrooms - Using standard 1-4+ if not dynamic, but usually beds are standard */}
                        <FilterSection
                            title={t('search.filters.bedrooms', 'Bedrooms')}
                            onReset={() => onUpdate('beds', [])}
                            hasSelection={filters.beds.length > 0}
                            t={t}
                        >
                            <div className="flex flex-wrap gap-2">
                                {['1', '2', '3', '4+'].map(bed => (
                                    <button
                                        key={bed}
                                        onClick={() => toggleFilter('beds', bed)}
                                        className={`
                                            w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border transition-all
                                            ${isSelected('beds', bed)
                                                ? 'bg-secondary text-white border-secondary'
                                                : 'bg-white text-gray-500 border-gray-200 hover:border-secondary'}
                                        `}
                                    >
                                        {bed}
                                    </button>
                                ))}
                            </div>
                        </FilterSection>

                        {/* Bathrooms */}
                        <FilterSection
                            title={t('search.filters.bathrooms', 'Bathrooms')}
                            onReset={() => onUpdate('baths', [])}
                            hasSelection={filters.baths.length > 0}
                            t={t}
                        >
                            <div className="flex flex-wrap gap-2">
                                {['1', '2', '3', '4+'].map(bath => (
                                    <button
                                        key={bath}
                                        onClick={() => toggleFilter('baths', bath)}
                                        className={`
                                            w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border transition-all
                                            ${isSelected('baths', bath)
                                                ? 'bg-secondary text-white border-secondary'
                                                : 'bg-white text-gray-500 border-gray-200 hover:border-secondary'}
                                        `}
                                    >
                                        {bath}
                                    </button>
                                ))}
                            </div>
                        </FilterSection>

                        {/* Construction Status */}
                        {meta.constructionStatuses && meta.constructionStatuses.length > 0 && (
                            <FilterSection
                                title={t('search.filters.constructionStatus', 'Construction Status')}
                                onReset={() => onUpdate('constructionStatus', [])}
                                hasSelection={filters.constructionStatus?.length > 0}
                                t={t}
                            >
                                <div className="space-y-2">
                                    {meta.constructionStatuses.map(status => (
                                        <label key={status.id} className="flex items-center gap-3 cursor-pointer group">
                                            <div className={`
                                                w-5 h-5 rounded border flex items-center justify-center transition-colors
                                                ${isSelected('constructionStatus', status.id)
                                                    ? 'bg-secondary border-secondary text-white'
                                                    : 'border-gray-300 group-hover:border-secondary'}
                                            `}>
                                                {isSelected('constructionStatus', status.id) && <Check className="w-3.5 h-3.5" />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={isSelected('constructionStatus', status.id)}
                                                onChange={() => toggleFilter('constructionStatus', status.id)}
                                            />
                                            <span className={`text-sm ${isSelected('constructionStatus', status.id) ? 'font-medium text-secondary' : 'text-gray-600'}`}>
                                                {t(status.labelKey, status.id.replace(/_/g, ' '))}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </FilterSection>
                        )}

                        {/* Finish Type */}
                        {meta.finishTypes && meta.finishTypes.length > 0 && (
                            <FilterSection
                                title={t('search.filters.finishType', 'Finish Type')}
                                onReset={() => onUpdate('finishType', [])}
                                hasSelection={filters.finishType?.length > 0}
                                t={t}
                            >
                                <div className="space-y-2">
                                    {meta.finishTypes.map(finish => (
                                        <label key={finish.id} className="flex items-center gap-3 cursor-pointer group">
                                            <div className={`
                                                w-5 h-5 rounded border flex items-center justify-center transition-colors
                                                ${isSelected('finishType', finish.id)
                                                    ? 'bg-secondary border-secondary text-white'
                                                    : 'border-gray-300 group-hover:border-secondary'}
                                            `}>
                                                {isSelected('finishType', finish.id) && <Check className="w-3.5 h-3.5" />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={isSelected('finishType', finish.id)}
                                                onChange={() => toggleFilter('finishType', finish.id)}
                                            />
                                            <span className={`text-sm ${isSelected('finishType', finish.id) ? 'font-medium text-secondary' : 'text-gray-600'}`}>
                                                {t(finish.labelKey, finish.id.replace(/_/g, ' '))}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </FilterSection>
                        )}

                        {/* Area Range */}
                        <div className="pt-2 pb-6 border-b border-gray-100">
                            <h3 className="font-bold text-secondary mb-3">{t('search.filters.area', 'Area')} (m²)</h3>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    placeholder={meta.minArea.toString()}
                                    value={filters.minArea}
                                    onChange={(e) => onUpdate('minArea', e.target.value)}
                                    className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary"
                                />
                                <span className="text-gray-400">-</span>
                                <input
                                    type="number"
                                    placeholder={meta.maxArea.toString()}
                                    value={filters.maxArea}
                                    onChange={(e) => onUpdate('maxArea', e.target.value)}
                                    className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary"
                                />
                            </div>
                        </div>

                        {/* Price Range */}
                        <div className="pt-2">
                            <h3 className="font-bold text-secondary mb-3">{t('search.filters.priceRange', 'Price Range')} ({t('common.egp', 'EGP')})</h3>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    placeholder={meta.minPrice.toLocaleString()}
                                    value={filters.minPrice}
                                    onChange={(e) => onUpdate('minPrice', e.target.value)}
                                    className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary"
                                />
                                <span className="text-gray-400">-</span>
                                <input
                                    type="number"
                                    placeholder={meta.maxPrice.toLocaleString()}
                                    value={filters.maxPrice}
                                    onChange={(e) => onUpdate('maxPrice', e.target.value)}
                                    className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};

const FilterSection = ({ title, children, onReset, hasSelection, t }: any) => {
    const [isOpen, setIsOpen] = useState(true);

    return (
        <div className="border-b border-gray-100 pb-6">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between mb-4 group"
            >
                <h3 className="font-bold text-secondary group-hover:text-primary transition-colors">{title}</h3>
                <div className="flex items-center gap-2">
                    {hasSelection && (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                onReset();
                            }}
                            className="text-xs font-semibold text-red-500 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                        >
                            {t('common.reset', 'Reset')}
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {isOpen && (
                <div className="animate-fade-in-down">
                    {children}
                </div>
            )}
        </div>
    );
};
